/**
 * Renders the appropriate Mantine input for a given column metadata.
 * Used inside MRT's edit form (modal mode).
 */
import { TextInput, Textarea, NumberInput, Checkbox } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import type { ColumnMeta } from "../api/types";

const NUMERIC = new Set(["int", "bigint", "smallint", "tinyint", "decimal", "numeric", "money", "smallmoney", "float", "real"]);
const DATE_TYPES = new Set(["date", "datetime", "datetime2", "smalldatetime", "datetimeoffset"]);
const LONG_TEXT = new Set(["text", "ntext", "xml"]);

export function DynamicField({
  column,
  value,
  onChange,
  disabled,
}: {
  column: ColumnMeta;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const label = column.name + (column.isNullable ? "" : " *");
  const dt = column.dataType.toLowerCase();
  const readOnly = disabled || column.isComputed || column.isIdentity;

  if (dt === "bit") {
    return (
      <Checkbox
        label={label}
        checked={Boolean(value)}
        onChange={(e) => onChange(e.currentTarget.checked)}
        disabled={readOnly}
      />
    );
  }

  if (NUMERIC.has(dt)) {
    return (
      <NumberInput
        label={label}
        value={(value as number | string | undefined) ?? ""}
        onChange={(v) => onChange(v)}
        disabled={readOnly}
        decimalScale={column.scale ?? undefined}
      />
    );
  }

  if (DATE_TYPES.has(dt)) {
    const asDate = value ? new Date(value as string) : null;
    return (
      <DateTimePicker
        label={label}
        value={asDate}
        onChange={(v) => onChange(v ? (v as Date).toISOString() : null)}
        disabled={readOnly}
        clearable
      />
    );
  }

  const isLong = LONG_TEXT.has(dt) || (column.maxLength != null && column.maxLength > 256) || column.maxLength === -1;
  if (isLong) {
    return (
      <Textarea
        label={label}
        value={(value as string | undefined) ?? ""}
        onChange={(e) => onChange(e.currentTarget.value)}
        disabled={readOnly}
        autosize
        minRows={3}
      />
    );
  }

  return (
    <TextInput
      label={label}
      value={(value as string | undefined) ?? ""}
      onChange={(e) => onChange(e.currentTarget.value)}
      disabled={readOnly}
      maxLength={column.maxLength ?? undefined}
    />
  );
}
